<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header :title="viewMeta.title" :icon="viewMeta.icon"/>

            <div class="columns mt-6">
                <div class="column is-pd pb-6">
                    <!-- The section is reachable while clock hours is off (deep
                         links, the switch flipped mid-session); the tools wait
                         for the switch -->
                    <certification-off-notice v-if="certificationsLoaded && !clockHoursOn" :certification-id="1">
                        Turn on Washington clock hours to see your hours, renewal progress and salary schedule here.
                    </certification-off-notice>

                    <div v-else-if="!pdSummary && pdLoading" class="has-text-white">
                        <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading your professional development...
                    </div>
                    <empty-state v-else-if="!pdSummary" icon="fa-triangle-exclamation" text="We couldn't load your professional development right now. Please try again in a moment."/>

                    <pd-overview v-else-if="currentView === 'overview'"/>
                    <pd-salary-view v-else-if="currentView === 'salary'"/>
                    <pd-renewal-view v-else-if="currentView === 'renewal'"/>
                    <pd-settings-view v-else-if="currentView === 'settings'"/>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import MainHeader from "../../components/framework/MainHeader";
    import EmptyState from "../../components/framework/EmptyState";
    import CertificationOffNotice from "../../components/dashboard/CertificationOffNotice";
    import PdOverview from "../../components/pd/PdOverview";
    import PdSalaryView from "../../components/pd/PdSalaryView";
    import PdRenewalView from "../../components/pd/PdRenewalView";
    import PdSettingsView from "../../components/pd/PdSettingsView";

    // Routed views under /professional-development/:view — siblings in the
    // section submenu (SidePanelNav), same pattern as /reports/:view
    const VIEWS = {
        '': {key: 'overview', title: 'Professional Development', icon: 'fa-arrow-trend-up'},
        salary: {key: 'salary', title: 'Salary Schedule', icon: 'fa-chart-line-up'},
        renewal: {key: 'renewal', title: 'Renewal Progress', icon: 'fa-calendar-check'},
        settings: {key: 'settings', title: 'Settings', icon: 'fa-sliders'},
    };

    /**
     * Professional Development: the clock-hour user's "where you stand"
     * section — hours earned and in progress, certificate renewal tracking,
     * and their district's salary schedule with what the next column is
     * worth. Replaces the Your Account > Professional Development settings
     * tab (career + renewal details are edited in place here).
     */
    export default {
        name: "ProfessionalDevelopment",
        components: {Framework, SidePanelNav, MainHeader, EmptyState, CertificationOffNotice, PdOverview, PdSalaryView, PdRenewalView, PdSettingsView},
        props: {
            // Route param (/professional-development/:view)
            view: {type: String, default: ''}
        },
        computed: {
            ...mapGetters(['pdSummary', 'pdLoading', 'certificationsLoaded', 'clockHoursOn']),
            viewMeta() {
                return VIEWS[this.view || ''] || VIEWS[''];
            },
            currentView() {
                return this.viewMeta.key;
            }
        },
        methods: {
            ...mapActions(['getPd']),
            checkView() {
                if (!VIEWS[this.view || '']) this.$router.replace({path: '/professional-development'}).catch(() => {});
            }
        },
        watch: {
            view() {
                this.checkView();
            }
        },
        mounted() {
            this.checkView();
            this.getPd().catch(() => {});
            if (!this.certificationsLoaded) this.$store.dispatch('getCertifications').catch(() => {});
        }
    };
</script>

<style lang="scss" scoped>
    // Flex children default to min-width:auto, so the salary grid's intrinsic
    // width would stretch the column past the panel instead of scrolling
    // inside its own container
    .column.is-pd {
        min-width: 0;
    }
</style>
